import { formatDate } from './formUtils';

/**
 * Resets the `isSelected` property to false for all calendar items.
 *
 * @param {Array<Object>} originalData - Array of calendar items.
 * @returns {Array<Object>} Updated array with all items deselected.
 */
export const resetCalendarSelection = (originalData) => {
  if (!originalData) return [];

  const originalDataCopy = [...originalData];

  return originalDataCopy.map((item) => ({
    ...item,
    isSelected: false,
  }));
};

/**
 * Toggles the `isSelected` state for an item based on the provided date.
 *
 * @param {Array<Object>} items - Array of calendar items.
 * @param {string|Date} currentDate - The date to toggle selection for.
 * @returns {Array<Object>} Updated array with toggled `isSelected` property.
 */
/**
 * Toggles the `isSelected` state for an item based on the provided date.
 * If no item matches the date, adds a new item with default values.
 *
 * @param {Array<Object>} items - Array of calendar items.
 * @param {string|Date} currentDate - The date to toggle selection for.
 * @returns {Array<Object>} Updated array with toggled `isSelected` property or new item added.
 */
export const toggleItemSelectionByDate = (items, currentDate) => {
  const formattedDate = formatDate(currentDate);

  const initialSelection = {
    selectedDate: formattedDate,
    hours: null,
    hours2: null,
    overTime: null,
    overTimeSelect: null,
    workType: null,
    isSelected: true,
  };

  const hasMatchingItem = items?.some(
    (item) => item.selectedDate === formattedDate
  );

  if (!hasMatchingItem) {
    return [...items, initialSelection];
  }

  return items?.map((item) => {
    if (item.selectedDate === formattedDate) {
      return {
        ...item,
        isSelected: !item.isSelected,
      };
    }
    return item;
  });
};

/**
 * Toggles the presence of a formatted date in the array of selected dates.
 * If the date is already selected, it removes it; otherwise, it adds it.
 *
 * @param {Array<string>} prevDates - Array of previously selected dates (formatted as strings).
 * @param {string|Date} currentDate - The date to toggle selection for.
 * @returns {Array<string>} Updated array of selected dates.
 */
export const toggleSelectedDate = (prevDates, currentDate) => {
  const formattedDate = formatDate(currentDate);

  const isAlreadySelected = prevDates.includes(formattedDate);

  return isAlreadySelected
    ? prevDates.filter((date) => date !== formattedDate)
    : [...prevDates, formattedDate];
};

/**
 * Toggles the `isSelected` state for an item based on the provided date.
 *
 * @param {Array<Object>} items - Array of calendar items.
 * @param {string|Date} currentDate - The date to toggle selection for.
 * @returns {Array<Object>} Updated array with toggled `isSelected` property.
 */
export const mergeCalendarItems = (items, formValues) => {
  if (!formValues) return;

  return items.map((item) => {
    const matchingNewDate = formValues.find(
      ({ selectedDate }) => selectedDate === item.selectedDate
    );

    if (matchingNewDate) {
      return {
        ...item,
        ...matchingNewDate,
      };
    }

    return item;
  });
};

/**
 * Merges properties from formValues into the existing items object.
 *
 * @param {Object} items - Original object with key-value pairs.
 * @param {Object} formValues - Object containing new values to merge.
 * @returns {Object} Updated object with merged values.
 */
export const mergeCalendarItemsObject = (items, formValues) => {
  if (!formValues) return items;

  return {
    ...items,
    ...Object.keys(formValues).reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(items, key)) {
        acc[key] = formValues[key];
      }
      return acc;
    }, {}),
  };
};

/**
 * Recursively serializes an object into a stable JSON string, ensuring:
 * - Object keys are sorted to maintain a consistent structure.
 * - Arrays are sorted to avoid order-based discrepancies.
 * - Handles nested objects and arrays correctly.
 *
 * @param {any} value - The value to be serialized (can be an object, array, or primitive).
 * @returns {string} A stable JSON string representation of the input value.
 */
export const stableJsonStringify = (value) => {
  if (Array.isArray(value)) {
    return JSON.stringify(value.map(stableJsonStringify).sort());
  } else if (value !== null && typeof value === 'object') {
    return JSON.stringify(
      Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          acc[key] = stableJsonStringify(value[key]);
          return acc;
        }, {})
    );
  }

  return JSON.stringify(value);
};

/**
 * Removes specified keys from each object in an array.
 *
 * @param {Array<Object>} array - The array of objects to process.
 * @param {string[]} keysToRemove - The keys to remove from each object.
 * @returns {Array<Object>} A new array with the specified keys removed from each object.
 */
export const removeKeysFromArray = (array, keysToRemove) => {
  return array.map((obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key]) => !keysToRemove.includes(key))
    );
  });
};

/**
 * Checks if the form data has changed compared to the original data.
 * @param {object} originalData - The original form data.
 * @param {object} updatedData - The currently updated form data.
 * @returns {boolean} - Returns `true` if the form data has changed, otherwise `false`.
 */
export const hasFormChanged = (originalData, updatedData) => {
  const removedKeys = ['overTimeSelect', 'isSelected'];

  const originalDataFormatted = {
    ...originalData,
    hoursByDay: removeKeysFromArray(originalData.hoursByDay, removedKeys),
  };

  const updatedDataFormatted = {
    ...updatedData,
    hoursByDay: removeKeysFromArray(updatedData.hoursByDay, removedKeys),
  };

  // ? INFO: debugging purposes
  // const dataOriginal = originalDataFormatted?.data;
  // const dataUpdate = updatedDataFormatted?.data;
  // console.debug({ dataOriginal, dataUpdate });

  const hasChanges =
    stableJsonStringify(originalDataFormatted) !==
    stableJsonStringify(updatedDataFormatted);

  return hasChanges;
};

/**
 * Ensures all objects in an array have the same keys,
 * filling missing values with an empty string.
 *
 * @param {Array<Object>} array - The array of objects to format.
 * @returns {Array<Object>} A new array with consistent keys across all objects.
 */
export const formatObjectsToMatch = (array) => {
  if (!array) return [];

  const allKeys = [...new Set(array.flatMap(Object.keys))];

  return array.map((obj) => {
    return allKeys.reduce((acc, key) => {
      acc[key] = obj[key] ?? '';
      return acc;
    }, {});
  });
};

/**
 * Checks if a selected date can be picked in the calendar.
 *
 * @param {Array} hoursByDay - Array of objects containing date and disabled status.
 * @param {string} currentDate - The current date to check.
 * @returns {boolean} - True if the current date is disabled, false otherwise.
 */
export const canSelectDateInCalendar = (hoursByDay, currentDate) => {
  const disabledDatesSet = getDisabledDates(hoursByDay);
  return disabledDatesSet.has(formatDate(currentDate));
};

/**
 * Returns a set of disabled dates.
 *
 * @param {Array} hoursByDay - Array of objects containing date and disabled status.
 * @returns {Set} - A set of dates that are disabled.
 */
const getDisabledDates = (hoursByDay) => {
  return new Set(
    hoursByDay
      .filter(({ disabled }) => disabled)
      .map(({ selectedDate }) => selectedDate)
  );
};

/**
 * Finds a work type object by its key in a nested structure.
 *
 * @param {Array} workTypes - List of work type objects, each with a possible `children` array.
 * @param {string} key - The unique key to search for.
 * @returns {Object|null} - The found work type object or null if not found.
 */
export const getWorkTypeByKey = (workTypes, key) => {
  for (const workType of workTypes) {
    // Skip invalid entries (plain strings or non-objects)
    if (typeof workType !== 'object' || workType === null) {
      continue;
    }

    if (workType.key === key) {
      return workType;
    }

    if (workType.children) {
      const foundInChildren = getWorkTypeByKey(workType.children, key);
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }

  return null;
};

/**
 * Transforms an array of variation drawer entries into a Map-compatible object for the backend.
 * Each entry should have { key, amount, currency, dateOfCreation }.
 *
 * @param {Array} drawerArray - Array of variation entries from the subdrawer form.
 * @returns {Object} - Object with keys as variation type keys and values as { amount, currency, dateOfCreation }.
 */
const transformVariationsDrawerToMap = (drawerArray) => {
  if (!drawerArray || !Array.isArray(drawerArray)) return {};

  return drawerArray.reduce((acc, item) => {
    if (item.key) {
      acc[item.key] = {
        amount: parseFloat(item.amount) || 0,
        currency: item.currency || 'rsd',
        dateOfCreation: item.dateOfCreation || null,
      };
    }
    return acc;
  }, {});
};

/**
 * Transforms salary-related data into a format suitable for backend processing.
 *
 * @param {Object} data - The updated salary data object.
 * @returns {Object} - The transformed salary data ready for backend.
 */
export const transformSalaryDataForBackend = ({ ...data }) => {
  const transformed = {
    ...data,
    company: data.company._id,
    employee: data.employee._id,
  };

  // Transform bonus variations drawer array to Map format for backend
  if (data.bonusDrawer && Array.isArray(data.bonusDrawer)) {
    transformed.bonusVariationsData = transformVariationsDrawerToMap(
      data.bonusDrawer
    );
    delete transformed.bonusDrawer;
  }

  // Transform suspension variations drawer array to Map format for backend
  if (
    data.suspensionVariationsDrawer &&
    Array.isArray(data.suspensionVariationsDrawer)
  ) {
    transformed.suspensionVariationsData = transformVariationsDrawerToMap(
      data.suspensionVariationsDrawer
    );
    delete transformed.suspensionVariationsDrawer;
  }

  // Transform non-monetary benefit variations drawer array to Map format for backend
  if (
    data.nonMonetaryBenefitDrawer &&
    Array.isArray(data.nonMonetaryBenefitDrawer)
  ) {
    transformed.nonMonetaryBenefitVariationsData =
      transformVariationsDrawerToMap(data.nonMonetaryBenefitDrawer);
    delete transformed.nonMonetaryBenefitDrawer;
  }

  // Extract reason from general NMB data
  if (data.generalNonMonetaryBenefitData?.reason) {
    transformed.nonMonetaryBenefitReason =
      data.generalNonMonetaryBenefitData.reason;
  }

  return transformed;
};
